import React, { useState, useRef, useEffect, useCallback } from 'react';
import Styled from 'styled-components';
import { Button, Tooltip } from 'antd';
import {
    FullscreenOutlined,
    FullscreenExitOutlined,
    DownloadOutlined,
    ExportOutlined,
    FilePdfOutlined,
} from '@ant-design/icons';

const catalogueUrl = `${process.env.PUBLIC_URL || ''}/documents/unique-products-catalogue.pdf`;

const PdfViewerWrapper = Styled.div`
    background: #0f172a;
    border: 1px solid #334155;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
    display: flex;
    flex-direction: column;
    text-align: left;
    transition: all 0.2s ease;

    a {
        text-decoration: none !important;
        color: inherit;
    }

    /* Compact Top Toolbar */
    .pdf-toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 6px 14px;
        background: #1e293b;
        border-bottom: 1px solid #334155;
        gap: 10px;
        flex-wrap: wrap;

        .toolbar-title-group {
            display: flex;
            align-items: center;
            gap: 8px;

            .badge-icon {
                width: 24px;
                height: 24px;
                border-radius: 6px;
                background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
                color: #ffffff;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 13px;
                flex-shrink: 0;
            }

            .pdf-badge {
                color: #e2e8f0;
                font-size: 12px;
                font-weight: 600;
            }
        }

        /* Right Actions */
        .toolbar-actions {
            display: flex;
            align-items: center;
            gap: 6px;

            .action-btn {
                background: #0f172a;
                border: 1px solid #475569;
                color: #e2e8f0;
                font-size: 11px;
                height: 28px;
                padding: 0 10px;
                border-radius: 6px;
                display: inline-flex;
                align-items: center;
                gap: 4px;
                transition: all 0.15s ease;

                &:hover {
                    background: #334155;
                    border-color: #94a3b8;
                    color: #ffffff;
                }

                &.btn-fullscreen {
                    background: #ef4444;
                    border-color: #ef4444;
                    color: #ffffff;
                    font-weight: 600;

                    &:hover {
                        background: #dc2626;
                        border-color: #dc2626;
                    }
                }
            }
        }
    }

    /* Direct PDF Viewer Body */
    .pdf-viewer-frame {
        position: relative;
        width: 100%;
        height: 640px;
        background: #1e293b;

        @media (max-width: 768px) {
            height: 480px;
        }

        iframe, object {
            width: 100%;
            height: 100%;
            border: none;
            display: block;
            background: #525659;
        }

        .fallback-message {
            position: absolute;
            inset: 0;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: 24px;
            text-align: center;
            color: #cbd5e1;
            background: #0f172a;

            p {
                margin: 0 0 16px 0;
                font-size: 14px;
                color: #94a3b8;
                max-width: 420px;
            }
        }
    }

    /* Fullscreen Overlay Mode */
    &.is-fullscreen {
        position: fixed !important;
        inset: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        max-height: 100vh !important;
        margin: 0 !important;
        border-radius: 0 !important;
        border: none !important;
        z-index: 999999 !important;

        .pdf-toolbar {
            padding: 10px 20px;
            background: #0f172a;
        }

        .pdf-viewer-frame {
            height: calc(100vh - 54px) !important;
            max-height: none !important;
        }
    }
`;

export default function CataloguePdfViewer() {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const containerRef = useRef(null);

    const toggleFullscreen = useCallback(() => {
        if (!isFullscreen) {
            setIsFullscreen(true);
            document.body.style.overflow = 'hidden';

            if (containerRef.current && containerRef.current.requestFullscreen) {
                containerRef.current.requestFullscreen().catch(() => {});
            }
        } else {
            setIsFullscreen(false);
            document.body.style.overflow = '';

            if (document.fullscreenElement && document.exitFullscreen) {
                document.exitFullscreen().catch(() => {});
            }
        }
    }, [isFullscreen]);

    useEffect(() => {
        const handleFullscreenChange = () => {
            if (!document.fullscreenElement) {
                setIsFullscreen(false);
                document.body.style.overflow = '';
            }
        };

        const handleKeyDown = (e) => {
            if (e.key === 'Escape' && isFullscreen) {
                toggleFullscreen();
            }
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            window.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
        };
    }, [isFullscreen, toggleFullscreen]);

    return (
        <PdfViewerWrapper
            ref={containerRef}
            className={isFullscreen ? 'is-fullscreen' : ''}
            aria-labelledby="pdf-catalogue-title"
        >
            {/* Compact Top Toolbar */}
            <div className="pdf-toolbar">
                <div className="toolbar-title-group">
                    <div className="badge-icon">
                        <FilePdfOutlined />
                    </div>
                    <span className="pdf-badge" id="pdf-catalogue-title">
                        Native PDF (22 Pages)
                    </span>
                </div>

                {/* Right Actions */}
                <div className="toolbar-actions">
                    <Tooltip title="Full Screen">
                        <Button
                            className="action-btn btn-fullscreen"
                            icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                            onClick={toggleFullscreen}
                            size="small"
                        >
                            {isFullscreen ? 'Exit' : 'Full'}
                        </Button>
                    </Tooltip>

                    <Tooltip title="Open PDF in a new browser tab">
                        <a href={catalogueUrl} target="_blank" rel="noopener noreferrer">
                            <Button className="action-btn" icon={<ExportOutlined />} size="small" />
                        </a>
                    </Tooltip>

                    <Tooltip title="Download original PDF file (43 MB)">
                        <a href={catalogueUrl} download>
                            <Button className="action-btn" icon={<DownloadOutlined />} size="small" />
                        </a>
                    </Tooltip>
                </div>
            </div>

            {/* Direct PDF iframe */}
            <div className="pdf-viewer-frame">
                <iframe
                    src={`${catalogueUrl}#view=Fit`}
                    title="Unique Products Catalogue PDF Viewer"
                    loading="lazy"
                    width="100%"
                    height="100%"
                >
                    <div className="fallback-message">
                        <p>
                            PDF preview is unavailable on this device browser. You can open it in a new tab or download it directly.
                        </p>
                        <a href={catalogueUrl} target="_blank" rel="noopener noreferrer">
                            <Button type="primary" icon={<ExportOutlined />} style={{ marginRight: 8 }}>
                                Open PDF
                            </Button>
                        </a>
                        <a href={catalogueUrl} download>
                            <Button icon={<DownloadOutlined />}>Download PDF (43 MB)</Button>
                        </a>
                    </div>
                </iframe>
            </div>
        </PdfViewerWrapper>
    );
}

